import { useRef, useState, useEffect, useCallback, useMemo, useId } from "react";
import classes from "./Equipment.module.css";
import { useBooking } from "../../store/bookingStore";
import { EquipmentContainer } from "./EquipmentContainer";
import { SmallModal } from "../UI/SmallModal/SmallModal";
import { BookingEquipmentDetailModal } from "./BookingEquipmentDetailModal";
import {
    normalizePaidEquipmentInput,
    resolvePaidEquipmentImageSrc,
    formatPricePerPiece,
    paidCategoryHeading,
    orderedCategoriesFromPaidItems,
} from "../../pages/StudioProfile/paidEquipmentHelpers";

import { useScrollToTop } from "../../util/scrollToTop";

const CARD_WIDTH_PX = 238;
const CARD_GAP_PX = 32;

function buildPaidBookingRows(paidEquipment) {
    const list = normalizePaidEquipmentInput(paidEquipment);
    return list.map((raw) => {
        const name = raw.nameModel || raw.name || raw.title || "Equipment";
        const id = raw.id != null ? String(raw.id) : name;
        const categoryKey = raw.category || "Sonstiges";
        const hasExplicitMax = raw.quantity != null || raw.count != null;
        const maxCount = hasExplicitMax
            ? Math.max(1, Number(raw.quantity ?? raw.count) || 1)
            : 1;
        const counted = maxCount > 1;
        return {
            id,
            equipmentItemId: id,
            name,
            categoryKey,
            maxCount,
            counted,
            price: formatPricePerPiece(raw.pricePerPiece ?? raw.price),
            image: resolvePaidEquipmentImageSrc(raw) || undefined,
            description: raw.description || "",
        };
    });
}

const ChevronLeft = ({ disabled }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden>
        <circle cx="23" cy="23" r="22.5" fill="#F7F7F9" stroke="#E2E8EF" />
        <line
            x1="26.6001"
            y1="14.601"
            x2="17.6015"
            y2="23.5996"
            stroke={disabled ? "#CED1D5" : "#1F1F1F"}
            strokeWidth="1.7"
        />
        <line
            x1="17.6256"
            y1="22.4215"
            x2="26.6241"
            y2="32.1699"
            stroke={disabled ? "#CED1D5" : "#1F1F1F"}
            strokeWidth="1.7"
        />
    </svg>
);

const ChevronRight = ({ disabled }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none" aria-hidden>
        <circle cx="23" cy="23" r="22.5" transform="rotate(180 23 23)" fill="white" stroke="#E2E8EF" />
        <line
            x1="19.3999"
            y1="31.399"
            x2="28.3985"
            y2="22.4004"
            stroke={disabled ? "#CED1D5" : "#1F1F1F"}
            strokeWidth="1.7"
        />
        <line
            x1="28.3744"
            y1="23.5785"
            x2="19.3759"
            y2="13.8301"
            stroke={disabled ? "#CED1D5" : "#1F1F1F"}
            strokeWidth="1.7"
        />
    </svg>
);

const ALL_CATEGORIES_VALUE = "__all__";

const CategoryChevron = ({ open }) => (
    <span
        className={`${classes.categoryTriggerChevron} ${open ? classes.categoryTriggerChevronOpen : ""}`}
        aria-hidden
    >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
    </span>
);

/** Optional bookable (paid) equipment step backed by studio API data. */
export const Equipment = ({ paidEquipment, pageTitle = "Equipment buchen", nextSlide }) => {
    const stepScrollRootRef = useRef(null);
    useScrollToTop({ delay: 50, rootRef: stepScrollRootRef });


    const { bookingData } = useBooking();
    const scrollerRef = useRef(null);
    const categoryFieldRef = useRef(null);
    const categoryListId = useId();
    const [atStart, setAtStart] = useState(true);
    const [atEnd, setAtEnd] = useState(true);
    /** True when the card row is wider than the viewport (horizontal scroll is needed). */
    const [needsHorizontalNav, setNeedsHorizontalNav] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES_VALUE);
    const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
    const [detailItem, setDetailItem] = useState(null);

    const allRows = useMemo(() => buildPaidBookingRows(paidEquipment), [paidEquipment]);

    const categoryOptions = useMemo(() => {
        const rawList = normalizePaidEquipmentInput(paidEquipment);
        return orderedCategoriesFromPaidItems(rawList);
    }, [paidEquipment]);

    const rows = useMemo(() => {
        if (categoryFilter === ALL_CATEGORIES_VALUE) return allRows;
        return allRows.filter((r) => r.categoryKey === categoryFilter);
    }, [allRows, categoryFilter]);

    const categoryTriggerLabel =
        categoryFilter === ALL_CATEGORIES_VALUE
            ? "Alle Kategorien"
            : paidCategoryHeading(categoryFilter);

    useEffect(() => {
        if (!categoryMenuOpen) return;
        const onDoc = (e) => {
            if (!categoryFieldRef.current?.contains(e.target)) setCategoryMenuOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setCategoryMenuOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [categoryMenuOpen]);

    const updateScrollState = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setAtStart(scrollLeft <= 2);
        setAtEnd(scrollLeft + clientWidth >= scrollWidth - 2);
        setNeedsHorizontalNav(scrollWidth > clientWidth + 2);
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (el) el.scrollLeft = 0;
        updateScrollState();
    }, [categoryFilter, rows.length, updateScrollState]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        updateScrollState();
        el.addEventListener("scroll", updateScrollState);
        const ro = new ResizeObserver(updateScrollState);
        ro.observe(el);
        window.addEventListener("resize", updateScrollState);
        return () => {
            el.removeEventListener("scroll", updateScrollState);
            ro.disconnect();
            window.removeEventListener("resize", updateScrollState);
        };
    }, [rows.length, updateScrollState]);

    const scrollStep = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        const delta = (CARD_WIDTH_PX + CARD_GAP_PX) * dir;
        el.scrollBy({ left: delta, behavior: "smooth" });
    };

    if (allRows.length === 0) return null;

    return (
        <div className={classes.equipmentRoot} ref={stepScrollRootRef}>
            <header className={classes.equipmentHeader}>
                <h2 className={`${classes.equipmentPageTitle} titleSemibold2`}>{pageTitle}</h2>
                <div ref={categoryFieldRef} className={`${classes.categoryField} regularMiddle`}>
                    <button
                        type="button"
                        id={`${categoryListId}-trigger`}
                        className={classes.categoryTrigger}
                        aria-haspopup="listbox"
                        aria-expanded={categoryMenuOpen}
                        aria-controls={categoryListId}
                        onClick={() => setCategoryMenuOpen((o) => !o)}
                    >
                        <span className={classes.categoryTriggerLabel}>{categoryTriggerLabel}</span>
                        <CategoryChevron open={categoryMenuOpen} />
                    </button>
                    {categoryMenuOpen ? (
                        <ul id={categoryListId} className={classes.categoryMenu} role="listbox">
                            <li className={classes.categoryMenuItem} role="none">
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={categoryFilter === ALL_CATEGORIES_VALUE}
                                    className={`${classes.categoryMenuButton} ${
                                        categoryFilter === ALL_CATEGORIES_VALUE ? classes.categoryMenuButtonActive : ""
                                    }`}
                                    onClick={() => {
                                        setCategoryFilter(ALL_CATEGORIES_VALUE);
                                        setCategoryMenuOpen(false);
                                    }}
                                >
                                    Alle Kategorien
                                </button>
                            </li>
                            {categoryOptions.map((key) => (
                                <li key={key} className={classes.categoryMenuItem} role="none">
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={categoryFilter === key}
                                        className={`${classes.categoryMenuButton} ${
                                            categoryFilter === key ? classes.categoryMenuButtonActive : ""
                                        }`}
                                        onClick={() => {
                                            setCategoryFilter(key);
                                            setCategoryMenuOpen(false);
                                        }}
                                    >
                                        {paidCategoryHeading(key)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            </header>

            <div className={classes.equipmentCarouselArea}>
                <div className={classes.viewport}>
                    <div ref={scrollerRef} className={classes.scroller}>
                        {rows.length === 0 ? (
                            <p className={`${classes.emptyCategory} regularMiddle`}>
                                In dieser Kategorie ist kein Equipment vorhanden.
                            </p>
                        ) : (
                            rows.map((item) => {
                                const booked = bookingData.equipment?.find(
                                    (e) =>
                                        (e.equipmentItemId && e.equipmentItemId === item.equipmentItemId) ||
                                        (!e.equipmentItemId && e.name === item.name)
                                );
                                const counts = item.counted
                                    ? Math.min(booked?.count ?? 0, item.maxCount)
                                    : 0;
                                const checked = Boolean(booked?.checked);
                                return (
                                    <EquipmentContainer
                                        key={item.id}
                                        equipmentItemId={item.equipmentItemId}
                                        name={item.name}
                                        maxCount={item.maxCount}
                                        counts={counts}
                                        price={item.price}
                                        image={item.image}
                                        counted={item.counted}
                                        checked={checked}
                                        onOpenDetails={() => setDetailItem(item)}
                                    />
                                );
                            })
                        )}
                    </div>
                </div>

                {rows.length > 0 && needsHorizontalNav ? (
                    <div className={classes.navRow}>
                        <button
                            type="button"
                            className={classes.navButton}
                            onClick={() => scrollStep(-1)}
                            disabled={atStart}
                            aria-label="Nach links scrollen"
                        >
                            <ChevronLeft disabled={atStart} />
                        </button>
                        <button
                            type="button"
                            className={classes.navButton}
                            onClick={() => scrollStep(1)}
                            disabled={atEnd}
                            aria-label="Nach rechts scrollen"
                        >
                            <ChevronRight disabled={atEnd} />
                        </button>
                    </div>
                ) : null}
            </div>
            <SmallModal
                style={{ outline: "none" }}
                open={Boolean(detailItem)}
                onBackdropClick={() => setDetailItem(null)}
                paidEquipmentModal
            >
                {detailItem ? (
                    <BookingEquipmentDetailModal
                        item={detailItem}
                        counts={
                            detailItem.counted
                                ? Math.min(
                                      bookingData.equipment?.find(
                                          (e) =>
                                              (e.equipmentItemId &&
                                                  e.equipmentItemId === detailItem.equipmentItemId) ||
                                              (!e.equipmentItemId && e.name === detailItem.name)
                                      )?.count ?? 0,
                                      detailItem.maxCount
                                  )
                                : 0
                        }
                        checked={Boolean(
                            bookingData.equipment?.find(
                                (e) =>
                                    (e.equipmentItemId &&
                                        e.equipmentItemId === detailItem.equipmentItemId) ||
                                    (!e.equipmentItemId && e.name === detailItem.name)
                            )?.checked
                        )}
                        onClose={() => setDetailItem(null)}
                    />
                ) : null}
            </SmallModal>
            <div className={classes.bookingTotal}>
                <button className={classes.weiterButton} onClick={nextSlide}>Weiter</button>
            </div>
        </div>
    );
};

export default Equipment;
